import axios from "axios";

// Resolve BACKEND_URL with a "never trust localhost" rule.
//
// We've seen Capacitor on Android serve from `https://localhost/`. If we use
// `window.location.origin` blindly, every API call hits Capacitor's local
// static-file server (which 404s for `/api/...`). My previous detection logic
// relied on `window.location.hostname === 'localhost'` being decided BEFORE
// the bundle started — that turned out to be unreliable on some MIUI builds.
//
// New rule, ironclad:
//   IF window.location.origin contains 'localhost' OR '127.0.0.1' OR is empty
//      → use REACT_APP_BACKEND_URL (baked into the build)
//   ELSE → use window.location.origin (real web deployment)
//
// This works regardless of timing because it checks the URL EVERY time API is
// computed and falls back the moment it sees `localhost`. No race condition.
function _resolveBackendUrl() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").trim();
  let origin = "";
  try {
    if (typeof window !== "undefined" && window.location && window.location.origin) {
      origin = window.location.origin;
    }
  } catch { /* SSR / test envs */ }

  const isBadOrigin =
    !origin
    || origin === "null"
    || origin.includes("localhost")
    || origin.includes("127.0.0.1")
    || origin.startsWith("file:")
    || origin.startsWith("capacitor:");
  return isBadOrigin ? envUrl : origin;
}

const BACKEND_URL = _resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;

// Native detection — kept for the few places that explicitly need it
// (withCredentials switch, share fallback, etc.). It's CONSISTENT with the
// BACKEND_URL logic: if we ended up using the env URL, we're native.
const _isCapacitorNative = BACKEND_URL !== ((typeof window !== "undefined" && window.location?.origin) || "");

// Send cookies on cross-origin requests (httpOnly session cookie set by
// /api/auth/login). Native build disables this — see CORS comments in earlier
// commits for the full reasoning (Cloudflare wildcard ACAO + credentials).
axios.defaults.withCredentials = !_isCapacitorNative;

const KEY_USER_ID = "quattro_user_id";
const KEY_NAME = "quattro_user_name";
const KEY_AUTHED = "quattro_authed";

export function isAuthed() {
  try { return localStorage.getItem(KEY_AUTHED) === "1"; } catch { return false; }
}
function setAuthed(flag, user_id) {
  try {
    if (flag) {
      localStorage.setItem(KEY_AUTHED, "1");
      if (user_id) localStorage.setItem(KEY_USER_ID, user_id);
    } else {
      localStorage.removeItem(KEY_AUTHED);
    }
  } catch {}
}

export function getOrCreateUserId() {
  let id = localStorage.getItem(KEY_USER_ID);
  if (!id) {
    id = "u_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY_USER_ID, id);
  }
  return id;
}

export function getStoredName() {
  return localStorage.getItem(KEY_NAME) || "";
}

export function setStoredName(name) {
  localStorage.setItem(KEY_NAME, name);
}

export async function fetchOrCreateProfile(name) {
  const user_id = getOrCreateUserId();
  const finalName = name || getStoredName() || "Player " + user_id.slice(-4);
  setStoredName(finalName);
  // Retry up to 4 times on transient failures. The Kubernetes ingress can
  // return a brief "404 page not found" for a few seconds while the backend
  // pod is recycling — without retries the user gets stuck on "Profile didn't
  // load" until they manually tap RESET. 401 short-circuits (real auth fail).
  let lastErr = null;
  const delays = [0, 500, 1500, 3500];
  for (let attempt = 0; attempt < delays.length; attempt++) {
    if (delays[attempt] > 0) await new Promise((r) => setTimeout(r, delays[attempt]));
    try {
      const res = await axios.post(`${API}/profile`, { user_id, name: finalName });
      if (res.data?.ads_removed_until) {
        try { localStorage.setItem("quattro_remove_ads", String(res.data.ads_removed_until)); } catch {}
      }
      return res.data;
    } catch (e) {
      lastErr = e;
      // Real auth failure — no point retrying.
      if (e?.response?.status === 401) throw e;
    }
  }
  throw lastErr;
}

export async function getProfile() {
  const user_id = getOrCreateUserId();
  const res = await axios.get(`${API}/profile/${user_id}`);
  if (res.data?.ads_removed_until) {
    try { localStorage.setItem("quattro_remove_ads", String(res.data.ads_removed_until)); } catch {}
  }
  return res.data;
}

export async function updateProfile(payload) {
  const user_id = getOrCreateUserId();
  const res = await axios.patch(`${API}/profile/${user_id}`, payload);
  return res.data;
}

export async function recordGame(won, bet = 100) {
  const user_id = getOrCreateUserId();
  const res = await axios.post(`${API}/games/result`, { user_id, won, bet });
  return res.data;
}

export async function fetchLeaderboard(limit = 20) {
  const res = await axios.get(`${API}/leaderboard`, { params: { limit } });
  return res.data;
}

// ----- Auth -----
// New BYO Google OAuth flow (Feb 2026). The backend handles the full OAuth
// round-trip — the frontend just redirects to /api/auth/google/login.
// After Google auth, the backend 302s back to "/" with ?signed_in=1.
export function getSignInUrl() {
  const anonymous = getOrCreateUserId();
  const params = new URLSearchParams();
  if (anonymous) params.set("anonymous_user_id", anonymous);
  return `${API}/auth/google/login?${params.toString()}`;
}

// Kept for backward compatibility with the legacy AuthCallback page;
// callers should be migrated to getSignInUrl() / getMe().
export function getEmergentRedirectUrl() {
  return getSignInUrl();
}

export async function getMe() {
  const res = await axios.get(`${API}/auth/me`);
  if (res.data?.user?.user_id) {
    localStorage.setItem(KEY_USER_ID, res.data.user.user_id);
    setAuthed(true, res.data.user.user_id);
  }
  return res.data;
}

export async function logout() {
  try { await axios.post(`${API}/auth/logout`); } catch {}
  setAuthed(false);
}
